import React from 'react';
import { Badge, Button, Card, Col, Flex, Row, theme, Typography } from 'antd';

const { Paragraph } = Typography;

type HelpOptionSubcardProps = React.PropsWithChildren<{
  title: string;
  description: React.ReactNode;
  badgeText?: string;
}>;

function HelpOptionSubcard({ title, description, badgeText, children }: HelpOptionSubcardProps) {
  const { token } = theme.useToken();
  const content = (
    <Card
      style={{ height: '100%' }}
      styles={{ header: { background: token.colorFillAlter }, body: {
        padding: `${token.padding} ${token.paddingLG}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      } }}
      title={title}
    >
      <Paragraph
        style={{ marginBottom: token.margin, textAlign: 'center', color: token.colorText }}
      >
        {description}
      </Paragraph>
      {children}
    </Card>
  );

  if (!badgeText) {
    return content;
  }

  return (
    <Badge.Ribbon text={badgeText} color={token.colorPrimary}>
      {content}
    </Badge.Ribbon>
  );
}

type HelpOptionsCardProps = {
  onIntegrationGuideClick: () => void;
  onContinueWithNexusClick: () => void;
  onScheduleCallClick: () => void;
};

function HelpOptionsCard({
  onIntegrationGuideClick,
  onContinueWithNexusClick,
  onScheduleCallClick,
}: HelpOptionsCardProps) {
  const { token } = theme.useToken();

  return (
    <Card
      title="Still having trouble? We're here to help"
      styles={{
        header: { padding: `${token.padding} ${token.paddingLG}`, fontWeight: token.fontWeightStrong, fontSize: token.fontSizeLG },
        body: { padding: `${token.padding} ${token.paddingLG}` },
      }}
    >
      <Flex vertical gap={16}>
        <Paragraph style={{ marginBottom: 0, color: token.colorText }}>
          Pick the option that works best for you.
        </Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24} xl={8}>
            <HelpOptionSubcard
              title="Troubleshoot with Nexus"
              description={<>Get personalized help, <br /> tailored to your stack.</>}
              badgeText="Recommended"
            >
              <Button type="default" onClick={onContinueWithNexusClick}>
                Continue with Nexus
              </Button>
            </HelpOptionSubcard>
          </Col>
          <Col xs={24} sm={24} md={24} xl={8}>
            <HelpOptionSubcard
              title="Review the integration guide"
              description={<>Double-check that everything  <br />  is set up correctly.</>}
            >
              <Button type="link" onClick={onIntegrationGuideClick}>
                Go to Integration guides
              </Button>
            </HelpOptionSubcard>
          </Col>
          <Col xs={24} sm={24} md={24} xl={8}>
            <HelpOptionSubcard
              title="Schedule a call"
              description={<>Schedule a free onboarding call <br /> with our support team.</>}
            >
              <Button type="link" onClick={onScheduleCallClick}>
                Contact Support
              </Button>
            </HelpOptionSubcard>
          </Col>
        </Row>
      </Flex>
    </Card>
  );
}

export default HelpOptionsCard;
